import React, { Component } from 'react';
import PropTypes from 'prop-types'
import Moment from 'moment';
import * as types from '../../../constants/actionTypes';
import { DATE_FORMAT } from '../../../constants/constants';
import Notification from '../../Common/Notification';
import LimitedModeTaskDetailDrawer from './LimitedModeTaskDetailDrawer';
import LocationTaskDetailDrawer from './LocationTaskDetailDrawer';
import { actions as locationTaskActions } from "../../../actions/locationTaskActions";
import { actions as filesActions } from '../../../actions/filesActions';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import './TaskDetail.scss';

export const MODES = {
    Limited: 1,
    Add: 2,
    Edit: 3
};

const INITIAL_COMMENT_SAMPLE_SIZE = 10;

export class TaskDetailManager extends Component {

    constructor(props, context) {
        super(props, context);

        const task = {
            additionalInfo: "",
            anticipatedEffort: "",
            baselineId: 0,
            baselineTaskId: 0,
            communityId: "",
            contactEmail: "",
            contactName: "",
            contingentId: "",
            contingentTaskName: "",
            contingentTaskStatus: "",
            contingentTaskCoreStatusId: 0,
            comment: "",
            comments: [],
            commentTotal: 0,
            communityName: "",
            createdByUserId: 0,
            createdDate: "",
            departmentId: "",
            departmentName: "",
            financialsBudget: "",
            financialsActual: "",
            files: [],
            history: [],
            id: 0,
            isActive: true,
            isEditingProjectCoach: false,
            isShowingBasicInfo: false,
            isHidden: false,
            isMilestone: false,
            isProjectCertified: false,
            isLocationTaskDateAdjustable: false,
            jobTitleId: 0,
            jobTitleName: "",
            links: [],
            locationId: 0,
            locationKey: '',
            modifiedByUserId: 0,
            modifiedDate: "",
            name: "",
            ordinal: 0,
            phaseId: "",
            phaseName: "",
            projectId: "",
            projectName: "",
            projectCoachId: 0,
            projectCoach: "",
            responsibleUserId: 0,
            startDate: "",
            startDaysOut: 0,
            startWeeks: 0,
            startDays: 0,
            statusId: "",
            steps: [],
            subscriptionId: 0,
            successorId: "",
            successorTasks: [],
            targetDate: "",
            targetDaysOut: 0,
            targetWeeks: 0,
            targetDays: 0,
            locationTaskTagIds: [],
            baselineTaskTags: [],
            translation: undefined
        };

        this.state = {
            blankTask: task,
            isLoading: false,
            isSaving: false,
            isFormValid: true,
            wasTaskAdded: false,
            task: task,
            isDropZoneActive: false,
            isSavingFile: false,
            isSavingLink: false,
            limitedModeFilters: {
                isStartDateActive: false,
                isTaskStepsActive: false,
                isProjectWidgetActive: false,
                isTaskFeedbackActive: false,
                isTaskDependenciesActive: false,
                isTaskFinancialsActive: false,
                allowLocationTaskBudgetEntry: false,
                allowLocationTaskDateChanges: false,
                statuses: [],
                responsibleUsers: [],
                completeStatusId: 0,
                mentionableUsers: [],
                taskTags: []
            },
            locationTaskDrawerFilters: {
                isStartDateActive: false,
                isTaskStepsActive: false,
                isProjectWidgetActive: false,
                isProjectIdRequiredForLocationTask: false,
                isTaskDependenciesActive: false,
                allowLocationTaskDateChanges: false,
                completeStatusId: 0,
                locationOpeningDate: '',
                statuses: [],
                responsibleUsers: [],
                departments: [],
                communities: [],
                projects: [],
                phases: [],
                contingentTasks: [],
                successorTasks: []
            }
        }

        this.dropzoneRef = React.createRef();
    }

    componentDidMount = async () => {
        if (this.props.taskId === 0) {
            this.triggerAddMode();
        } else {
            this.triggerLimitedMode(this.props.taskId);
        }
    }

    componentDidUpdate(prevProps) {
        /**
         * Here we assume that the task ID has been updated because a user
         * clicked on a new row in some task list view.  This should trigger a
         * different task to be loaded, allowing users to quickly navigate
         * through the details of multiple tasks in a list without explicitly
         * closing and reopening a drawer.
         * 
         * We also assume that it's impossible to open the Add/Edit mode drawer
         * via this behavior, since Edit Mode can only be triggered via an icon
         * on Limited Mode and Add Mode can only be triggered via an Add Task
         * button, which is typically in the upper right corner, and thus hidden
         * by any of these managed task detail drawers, which are already open.
         */
        if (this.props.taskId !== prevProps.taskId) {
            this.triggerLimitedMode(this.props.taskId);
        }
    }

    triggerLimitedMode = async (taskId, isRedirect = false) => {
        if (this.state.currentMode === MODES.Edit & isRedirect === false) {
            /*
             * If someone just canceled the Edit mode drawer, and is being taken
             * back to the limited mode drawer, we already have all the info we
             * need to render the limited mode drawer, because the Edit mode
             * drawer was launched from the limited mode drawer
             * And isRedirect has to be false because we are simply going back to
             * LimitedModeDrawer of the same task, instead of being redirected to another task
             * 
             */
            this.setState({ currentMode: MODES.Limited });
        } else {
            try {
                // Do not await, fire and forget so it doesn't hold up drawer loading
                this.loadMentionableUsers();

                /**
                 * If a new task was just added, then limited mode is triggered with
                 * the new task ID, but the task in state is still the one prepared
                 * for add mode, so we need to reset it here to make sure it doesn't
                 * set render with add mode default values
                 */
                let task = { ...this.state.task };
                if (taskId !== this.state.task.id) {
                    task = { ...this.state.blankTask };
                }

                this.setState({ isLoading: true, currentMode: MODES.Limited, task: task });

                await this.loadTaskDetails(taskId);
                await this.loadLimitedModeFilters();
                await this.loadComments(taskId, INITIAL_COMMENT_SAMPLE_SIZE);

                this.filterTaskTags();
                this.setState({ isLoading: false });
            } catch (error) {
                Notification("error", "Error", error);
                this.props.onCancel();
            }
        }
    }

    triggerAddMode = async () => {
        try {
            this.setState({
                isLoading: true,
                currentMode: MODES.Add,
                task: {
                    ...this.state.task,
                    // When adding a new task, we initialize the links list with
                    // a single empty link value to be edited
                    links: [{
                        id: 0,
                        name: '',
                        url: '',
                        isActive: true,
                        ordinal: 0,
                        isFile: false
                    }],
                    // New tasks are by default assigned to the user creating
                    // the task
                    responsibleUserId: this.props.userId,
                    locationKey: this.props.locationKey,
                    isLocationTaskDateAdjustable: (this.state.limitedModeFilters.isProjectWidgetActive === true) ? false : true,
                    allowLocationTaskDateChanges: true
                }
            });
            await this.loadLocationTaskDrawerFilters();
            this.setState({ isLoading: false });
        } catch (error) {
            Notification("error", "Error", error);
            this.props.onCancel();
        }
    }

    triggerEditMode = async () => {
        try {
            this.setState({ isLoading: true, currentMode: MODES.Edit });
            // Edit mode can only be launched from Limited Mode, so we already
            // have all of the task details loaded.  We just need to make sure
            // we have all of the location task drawer filters loaded
            await this.loadLocationTaskDrawerFilters();
            this.setState({ isLoading: false });
        } catch (error) {
            Notification("error", "Error", error);
            this.props.onCancel();
        }
    }

    loadMentionableUsers = async () => {
        try {
            const response = await this.props.locationTaskActions.listMentionableUsers(this.props.locationKey);
            if (response && response.type === types.SEARCH_LOCATION_TASK_MENTIONABLE_USERS_SUCCESS) {
                this.setState((prevState) => ({
                    limitedModeFilters: {
                        ...prevState.limitedModeFilters,
                        mentionableUsers: response.payload.users
                    }
                }));
            }
        } catch (error) {
            // mentionable users are optional; ignore load failures
        }
    }

    /**
     * This single task details API end-point is used to load a task, regardless
     * of what kind of task or which mode, at least for now.  Yes, it loads
     * potentially unused details from a task that won't be changed, but it also
     * allows for switching between modes more quickly, which I believe will be
     * a common practice.
     */
    loadTaskDetails = async (taskId) => {
        const response = await this.props.locationTaskActions.loadLocationTask(taskId);
        if (response.type === types.LOAD_LOCATION_TASK_SUCCESS) {
            const startDate = (response.payload.locationTask.startDate) ? Moment.utc(response.payload.locationTask.startDate).format(DATE_FORMAT) : null;
            const targetDate = (response.payload.locationTask.targetDate) ? Moment.utc(response.payload.locationTask.targetDate).format(DATE_FORMAT) : null;
            const links = (response.payload.links && response.payload.links.length > 0) ? response.payload.links : this.state.task.links;
            const files = (response.payload.files && response.payload.files.length > 0) ? response.payload.files : this.state.task.files
            const coachId = response.payload.projectCoachId ? response.payload.projectCoachId : 0;
            const coachName = response.payload.projectCoach ? response.payload.projectCoach : '';

            this.setState({
                ...this.state,
                task: {
                    ...this.state.task,
                    ...response.payload.locationTask,
                    startDate: startDate,
                    targetDate: targetDate,
                    communityName: response.payload.communityName,
                    departmentName: response.payload.departmentName,
                    jobTitleName: response.payload.jobTitleName,
                    phaseName: response.payload.phaseName,
                    projectName: response.payload.projectName,
                    contingentTaskName: response.payload.contingentTaskName,
                    contingentTaskStatus: response.payload.contingentTaskStatus,
                    contingentTaskCoreStatusId: response.payload.contingentTaskCoreStatusId,
                    successorTasks: response.payload.successorTasks,
                    baselineId: response.payload.baselineId,
                    steps: response.payload.steps.map((s, i) => ({
                        ...s,
                        ordinal: i + 1,
                        isEditing: false
                    })),
                    links: links,
                    files: files,
                    isProjectCertified: response.payload.isProjectCertified,
                    projectCoach: coachName,
                    projectCoachId: coachId,
                    locationKey: response.payload.locationKey,
                    locationOpeningDate: response.payload.locationOpeningDate,
                    locationTaskTagIds: response.payload.locationTaskTagIds,
                    baselineTaskTags: response.payload.baselineTaskTags
                }
            });
        } else if (response.type === types.LOAD_LOCATION_TASK_FAILURE) {
            Notification("error", "Error", response.payload.message);
            this.props.onCancel();
        }
    }

    loadLimitedModeFilters = async () => {
        try {
            const response = await this.props.locationTaskActions.loadLimitedModeFilters(this.props.taskId);
            if (response.type === types.LOAD_LIMITED_MODE_FILTERS_SUCCESS) {
                this.setState((prevState) => ({
                    limitedModeFilters: {
                        ...prevState.limitedModeFilters,
                        isStartDateActive: response.payload.isStartDateActive,
                        isTaskStepsActive: response.payload.isTaskStepsActive,
                        isProjectWidgetActive: response.payload.isProjectWidgetActive,
                        isTaskFeedbackActive: response.payload.isTaskFeedbackActive,
                        isTaskDependenciesActive: response.payload.isTaskDependenciesActive,
                        isTaskFinancialsActive: response.payload.isTaskFinancialsActive,
                        allowLocationTaskBudgetEntry: response.payload.allowLocationTaskBudgetEntry,
                        allowLocationTaskDateChanges: response.payload.allowLocationTaskDateChanges,
                        statuses: response.payload.statuses,
                        completeStatusId: response.payload.completeStatusId,
                        taskTags: response.payload.taskTags
                    }
                }));
            } else if (response.type === types.LOAD_LIMITED_MODE_FILTERS_FAILURE) {
                Notification("error", "Error", response.payload.message);
                this.props.onCancel();
            }
        } catch (error) {
            Notification("error", "Error", "Failed to load filters. Please try again later.");
            this.props.onCancel();
        }
    }

    loadLocationTaskDrawerFilters = async () => {
        const response = await this.props.locationTaskActions.loadLocationTaskDrawerFilters(this.props.taskId, this.props.locationKey);
        if (response.type === types.LOAD_LOCATION_TASK_DRAWER_FILTERS_SUCCESS) {
            this.setState({
                ...this.state,
                locationTaskDrawerFilters: {
                    ...this.state.locationTaskDrawerFilters,
                    isStartDateActive: response.payload.isStartDateActive,
                    isTaskStepsActive: response.payload.isTaskStepsActive,
                    isProjectWidgetActive: response.payload.isProjectWidgetActive,
                    isProjectIdRequiredForLocationTask: response.payload.isProjectIdRequiredForLocationTask,
                    isTaskDependenciesActive: response.payload.isTaskDependenciesActive,
                    allowLocationTaskDateChanges: response.payload.allowLocationTaskDateChanges,
                    completeStatusId: response.payload.completeStatusId,
                    locationOpeningDate: response.payload.locationOpeningDate,
                    statuses: response.payload.statuses,
                    departments: response.payload.departments,
                    communities: response.payload.communities,
                    projects: response.payload.projects,
                    phases: response.payload.phases,
                    contingentTasks: response.payload.contingentTasks,
                    successorTasks: response.payload.contingentTasks // successor and contingent drop-downs have same possible values
                }
            }, () => {
                // When loading the add mode drawer, automatically set the status
                // to the default not started status for this subscription and set the
                // responsible user to the current user
                if (this.state.currentMode === MODES.Add) {
                    this.setState((prevState) => ({
                        ...prevState,
                        task: {
                            ...prevState.task,
                            statusId: response.payload.defaultNotStartedStatusId
                        }
                    }));
                }
            });
        } else if (response.type === types.LOAD_LOCATION_TASK_DRAWER_FILTERS_FAILURE) {
            Notification("error", "Error", response.payload.message);
            this.props.onCancel();
        }
    }

    loadComments = async (taskId, commentSampleSize) => {
        const response = await this.props.locationTaskActions.loadComments(taskId, commentSampleSize);
        if (response.type === types.LOAD_LOCATION_TASK_COMMENTS_SUCCESS) {
            this.setState({
                ...this.state,
                task: {
                    ...this.state.task,
                    comments: response.payload.comments || [],
                    commentTotal: response.payload.total
                }
            });
        } else if (response.type === types.LOAD_LOCATION_TASK_COMMENTS_FAILURE) {
            Notification("error", "Error", response.payload.message);
            this.props.onCancel();
        }
    }

    loadHistory = async (taskId) => {
        const response = await this.props.locationTaskActions.loadHistory(taskId);
        if (response.type === types.LOAD_LOCATION_TASK_HISTORY_SUCCESS) {
            this.setState({
                ...this.state,
                task: {
                    ...this.state.task,
                    history: response.payload.histories
                }
            });
        } else if (response.type === types.LOAD_LOCATION_TASK_HISTORY_FAILURE) {
            Notification("error", "Error", response.payload.message);
        }
    }

    onTaskChange = (task) => {
        this.setState({
            ...this.state,
            task: task
        });
    }

    showContingentDrawer = (e) => {
        e.preventDefault();
        this.triggerLimitedMode(this.state.task.contingentId, true);
    }

    showPrereqDrawer = (e, taskId) => {
        e.preventDefault();
        this.triggerLimitedMode(taskId, true);
    }

    onLimitedModeComplete = () => {
        this.props.onComplete();
    }

    onLimitedModeCancel = () => {
        if (this.state.wasTaskAdded) {
            this.props.onComplete();
        } else {
            this.props.onCancel();
        }
    }

    onLocationTaskDetailDrawerComplete = (newTaskId) => {
        if (this.state.currentMode === MODES.Add && newTaskId > 0) {
            /**
             * When a user finishes adding a new task, we immediately load
             * that new task in limited mode, so they can finish editing
             * the fields not available on the LocationTaskDetailDrawer
             * 
             * We also need to indicate that a task was added, so that if the
             * user decides to just cancel the limited view, the manager can
             * call props.onComplete instead of prop.onCancel, which helps the
             * calling page decide if it should reload its list of tasks to 
             * possibly include the newly added task.
             */
            this.setState({ wasTaskAdded: true });
            this.triggerLimitedMode(newTaskId);
        } else {
            /**
             * When edit mode has saved, we close all task detail drawers
             * and return to the caller of TaskDetailManager
             */
            this.props.onComplete();
        }
    }

    onLocationTaskDetailDrawerCancel = () => {
        /**
         * If the user cancels the Edit mode drawer, which can only be launched
         * from the Limited Mode drawer, take them back to the Limited Mode
         * drawer
         */
        if (this.state.currentMode === MODES.Edit) {
            this.triggerLimitedMode(this.state.task.id);
        } else {
            this.props.onCancel();
        }
    }

    filterTaskTags = () => {
        // If there are baselineTaskTags associated with this LocationTask, remove the baselineTaskTags from TaskTags options array
        if (this.state.task.baselineTaskTags && this.state.task.baselineTaskTags.length > 0) {
            const baselineTaskTagIds = this.state.task.baselineTaskTags.map(tag => tag.taskTagId);
            const updatedTaskTags = this.state.limitedModeFilters.taskTags.filter(tag => !baselineTaskTagIds.includes(tag.id));
            this.setState({
                limitedModeFilters: {
                    ...this.state.limitedModeFilters,
                    taskTags: updatedTaskTags
                }
            });
        }
    }

    render() {
        return (
            <div className="task-detail">
                {
                    this.state.currentMode === MODES.Limited &&
                    <LimitedModeTaskDetailDrawer
                        task={this.state.task}
                        isLoading={this.state.isLoading}
                        filters={this.state.limitedModeFilters}
                        responsibleUsers={this.props.responsibleUsers}
                        userId={this.props.userId}
                        customerRole={this.props.customerRole}
                        onTaskChange={this.onTaskChange}
                        loadComments={this.loadComments}
                        loadHistory={this.loadHistory}
                        locationTaskActions={this.props.locationTaskActions}
                        filesActions={this.props.filesActions}
                        showContingentDrawer={this.showContingentDrawer}
                        showPrereqDrawer={this.showPrereqDrawer}
                        showEditMode={this.triggerEditMode}
                        onComplete={this.onLimitedModeComplete}
                        onCancel={this.onLimitedModeCancel}
                        isMobile={this.props.isMobile}
                    />
                }
                {
                    (this.state.currentMode === MODES.Add || this.state.currentMode === MODES.Edit) &&
                    <LocationTaskDetailDrawer
                        task={this.state.task}
                        isLoading={this.state.isLoading}
                        filters={this.state.locationTaskDrawerFilters}
                        responsibleUsers={this.props.responsibleUsers}
                        userId={this.props.userId}
                        onTaskChange={this.onTaskChange}
                        locationTaskActions={this.props.locationTaskActions}
                        filesActions={this.props.filesActions}
                        showContingentDrawer={this.showContingentDrawer}
                        showPrereqDrawer={this.showPrereqDrawer}
                        onComplete={this.onLocationTaskDetailDrawerComplete}
                        onCancel={this.onLocationTaskDetailDrawerCancel}
                        isMobile={this.props.isMobile}
                    />
                }
            </div>
        )
    }
}

TaskDetailManager.propTypes = {
    // Task
    taskId: PropTypes.number.isRequired,
    locationKey: PropTypes.string.isRequired,
    responsibleUsers: PropTypes.array.isRequired,

    // User
    userId: PropTypes.number.isRequired,
    customerRole: PropTypes.string.isRequired,

    // Drawer Interface so manager can behave like a drawer, even though it
    // technically manages multiple drawers
    onComplete: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,

    // API Functionality
    filesActions: PropTypes.object.isRequired,
    locationTaskActions: PropTypes.object.isRequired,

    // funky stuff
    isMobile: PropTypes.bool.isRequired
};


function mapStateToProps(state) {
    return {
        userRole: state.user.customerRole,
        userId: state.user.id
    };
}

const mapDispatchToProps = dispatch => {
    return {
        filesActions: bindActionCreators(filesActions, dispatch),
        locationTaskActions: bindActionCreators(locationTaskActions, dispatch)
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(TaskDetailManager);
